import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import {
  FlipkartCampaignBudgetCell,
  FlipkartCampaignDateCell,
} from '@/components/flipkart/FlipkartCampaignEditCells';
import { FlipkartSynopsisMetricValue } from '@/components/flipkart/FlipkartSynopsisMetricValue';
import {
  DEFAULT_FLIPKART_AD_GROUP_STATUS,
  DEFAULT_FLIPKART_CAMPAIGN_STATUS,
  FLIPKART_AD_GROUP_STATUSES,
  FLIPKART_SYNOPSIS_METRICS,
  filterSynopsisGroups,
  filterSynopsisGroupsByStatus,
  flipkartSynopsisStatusOptions,
  formatFlipkartSynopsisMetric,
  sortSynopsisGroups,
  totalsFromGroups,
  type FlipkartSynopsisGroup,
  type FlipkartSynopsisSortKey,
} from '@/components/flipkart/flipkartSynopsisMetrics';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { VirtualizedTable } from '@/components/ui/VirtualizedTableBody';
import { WORKSPACE_PRODUCT_IDENTITY_COL_CLASS } from '@/components/workspace/WorkspaceProductIdentityCell';
import {
  FilterSearch,
  FilterSelect,
  SelectionCheckbox,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import {
  WORKSPACE_CHECKBOX_CELL_LAYOUT_CLASS,
  WORKSPACE_CHECKBOX_COL_SIZE_CLASS,
} from '@/components/workspace/workspaceStickyColumns';
import { cn } from '@/utils/cn';
import { formatIsoDisplayDate } from '@/api/ruleRecommendations';

/** Label / secondary columns: same 24rem identity track Amazon product cells use. */
const FLIPKART_LABEL_COL_CLASS = WORKSPACE_PRODUCT_IDENTITY_COL_CLASS;

/** Fixed checkbox column: same width + padding for header, rows, and totals. */
const FLIPKART_CHECKBOX_COL_CLASS = cn(
  WORKSPACE_CHECKBOX_COL_SIZE_CLASS,
  WORKSPACE_CHECKBOX_CELL_LAYOUT_CLASS
);

function FlipkartCheckboxCell({
  isHeader = false,
  children,
}: {
  isHeader?: boolean;
  children?: ReactNode;
}) {
  const content =
    children != null ? (
      <span className="flex h-full w-full items-center justify-center">{children}</span>
    ) : null;

  if (isHeader) {
    return <th className={cn(workspaceTableClass.th, FLIPKART_CHECKBOX_COL_CLASS)}>{content}</th>;
  }
  return <td className={cn(workspaceTableClass.td, FLIPKART_CHECKBOX_COL_CLASS)}>{content}</td>;
}

const CAMPAIGN_META_COLUMN_COUNT = 4;

/** Compare-on metric cells are three lines. Virtual rows use this estimate. */
const SYNOPSIS_ROW_HEIGHT = 72;

/** Six placement bids with an optional floor line. Virtual rows are not measured. */
const PLACEMENT_ROW_HEIGHT = 140;

/** Small breakdowns stay fully mounted. Larger ones only paint the scroll window. */
const SYNOPSIS_VIRTUALIZE_ABOVE = 50;

export type FlipkartSynopsisBulkAction = {
  id: string;
  label: string;
  disabled?: boolean;
};

export type FlipkartSynopsisSelection = {
  selectedIds: string[];
  /** Synced entity ids. Rows outside this set cannot be selected. */
  selectableIds: ReadonlySet<string>;
  onToggleId: (id: string, selected: boolean) => void;
  onToggleVisible: (ids: string[], selected: boolean) => void;
  onClear: () => void;
  actions: FlipkartSynopsisBulkAction[];
  onAction: (actionId: string) => void;
  actionDisabled?: boolean;
  /** Accessible name for a row that is not in the synced catalog. */
  unsyncedLabel?: (label: string) => string;
};

export function FlipkartSynopsisTable({
  title,
  labelHeader,
  secondaryHeader,
  groups,
  campaignMeta = false,
  onUpdateCampaign,
  selection,
  showPlacements = false,
  adGroupStatus = false,
  unavailableMessage,
  isLoading = false,
  isError = false,
  errorMessage,
  onRetry,
}: {
  title: string;
  labelHeader: string;
  secondaryHeader?: string;
  /** Null means this breakdown is not stored yet. */
  groups: FlipkartSynopsisGroup[] | null;
  /** Campaign entity columns + LIVE status filter (Flipkart Campaigns table only). */
  campaignMeta?: boolean;
  onUpdateCampaign?: (
    campaignId: string,
    patch: {
      daily_budget?: number;
      start_date?: string | null;
      end_date?: string | null;
    }
  ) => Promise<void>;
  selection?: FlipkartSynopsisSelection;
  /** Placement bids column. Used by the Ad Groups table. */
  showPlacements?: boolean;
  /** SERVICEABLE status filter. Used by the Ad Groups table. */
  adGroupStatus?: boolean;
  unavailableMessage: string;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
}) {
  const [query, setQuery] = useState('');
  const statusFiltering = campaignMeta || adGroupStatus;
  const [statusFilter, setStatusFilter] = useState(
    campaignMeta
      ? DEFAULT_FLIPKART_CAMPAIGN_STATUS
      : adGroupStatus
        ? DEFAULT_FLIPKART_AD_GROUP_STATUS
        : 'all'
  );
  const [sortKey, setSortKey] = useState<FlipkartSynopsisSortKey>('spend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const searchId = `flipkart-synopsis-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const statusId = `${searchId}-status`;
  const columnCount =
    FLIPKART_SYNOPSIS_METRICS.length +
    1 +
    (secondaryHeader ? 1 : 0) +
    (campaignMeta ? CAMPAIGN_META_COLUMN_COUNT : 0) +
    (showPlacements ? 1 : 0) +
    (selection ? 1 : 0);

  const statusOptions = useMemo(() => {
    if (adGroupStatus) return [...FLIPKART_AD_GROUP_STATUSES];
    if (groups && campaignMeta) return flipkartSynopsisStatusOptions(groups);
    return [];
  }, [adGroupStatus, campaignMeta, groups]);

  const visible = useMemo(() => {
    if (!groups) return [];
    const statusFiltered = statusFiltering
      ? filterSynopsisGroupsByStatus(groups, statusFilter)
      : groups;
    return sortSynopsisGroups(filterSynopsisGroups(statusFiltered, query), sortKey, sortDir);
  }, [groups, query, sortDir, sortKey, statusFilter, statusFiltering]);

  const totals = useMemo(() => (visible.length > 0 ? totalsFromGroups(visible) : null), [visible]);
  const visibleSelectableIds = selection
    ? visible.map((group) => group.key).filter((id) => selection.selectableIds.has(id))
    : [];
  const allVisibleSelected =
    visibleSelectableIds.length > 0 &&
    visibleSelectableIds.every((id) => selection?.selectedIds.includes(id));

  const emptyMessage =
    groups == null
      ? unavailableMessage
      : groups.length === 0
        ? 'No rows in this date range.'
        : visible.length === 0
          ? statusFiltering && statusFilter !== 'all'
            ? 'No rows match the filters.'
            : 'No rows match the search.'
          : null;

  const toggleSort = (key: FlipkartSynopsisSortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'label' || key === 'secondary' ? 'asc' : 'desc');
  };

  const virtualize = visible.length > SYNOPSIS_VIRTUALIZE_ABOVE;

  const tableHead = (
    <thead>
      <tr>
        {selection ? (
          <FlipkartCheckboxCell isHeader>
            <SelectionCheckbox
              checked={allVisibleSelected}
              disabled={visibleSelectableIds.length === 0}
              onChange={(checked) => selection.onToggleVisible(visibleSelectableIds, checked)}
              label={`Select visible ${title.toLowerCase()}`}
            />
          </FlipkartCheckboxCell>
        ) : null}
        <SortHead
          label={labelHeader}
          sortKey="label"
          activeKey={sortKey}
          dir={sortDir}
          onSort={toggleSort}
          className={FLIPKART_LABEL_COL_CLASS}
        />
        {secondaryHeader ? (
          <SortHead
            label={secondaryHeader}
            sortKey="secondary"
            activeKey={sortKey}
            dir={sortDir}
            onSort={toggleSort}
            className={FLIPKART_LABEL_COL_CLASS}
          />
        ) : null}
        {showPlacements ? (
          <th className={cn(workspaceTableClass.th, 'min-w-[28rem]')}>Placements</th>
        ) : null}
        {campaignMeta ? (
          <>
            <th className={workspaceTableClass.th}>Status</th>
            <th className={cn(workspaceTableClass.th, 'text-right')}>Daily budget</th>
            <th className={workspaceTableClass.th}>Start date</th>
            <th className={workspaceTableClass.th}>End date</th>
          </>
        ) : null}
        {FLIPKART_SYNOPSIS_METRICS.map((metric) => (
          <SortHead
            key={metric.key}
            label={metric.label}
            sortKey={metric.key}
            activeKey={sortKey}
            dir={sortDir}
            onSort={toggleSort}
            align="right"
          />
        ))}
      </tr>
    </thead>
  );

  const renderGroupRow = (group: FlipkartSynopsisGroup) => (
    <tr key={group.key} className={workspaceTableClass.rowHover}>
      {selection ? (
        <FlipkartCheckboxCell>
          <SelectionCheckbox
            checked={selection.selectedIds.includes(group.key)}
            disabled={!selection.selectableIds.has(group.key)}
            onChange={(checked) => selection.onToggleId(group.key, checked)}
            label={
              selection.selectableIds.has(group.key)
                ? `Select ${group.label}`
                : (selection.unsyncedLabel?.(group.label) ??
                  `Sync before selecting ${group.label}`)
            }
          />
        </FlipkartCheckboxCell>
      ) : null}
      <td
        className={cn(
          workspaceTableClass.td,
          FLIPKART_LABEL_COL_CLASS,
          'font-medium text-foreground'
        )}
        title={group.label || undefined}
      >
        {group.label}
      </td>
      {secondaryHeader ? (
        <td
          className={cn(
            workspaceTableClass.td,
            FLIPKART_LABEL_COL_CLASS,
            'text-foreground-muted'
          )}
          title={group.secondary || undefined}
        >
          {group.secondary || '—'}
        </td>
      ) : null}
      {showPlacements ? (
        <td
          className={cn(
            workspaceTableClass.td,
            'min-w-[28rem] max-w-none overflow-visible whitespace-normal align-top text-clip'
          )}
        >
          <FlipkartPlacementsCell placements={group.placements} />
        </td>
      ) : null}
      {campaignMeta ? (
        <>
          <td className={workspaceTableClass.td}>{group.status || '—'}</td>
          <td className={cn(workspaceTableClass.td, 'text-right')}>
            {group.campaignEditable && onUpdateCampaign ? (
              <FlipkartCampaignBudgetCell
                displayValue={formatFlipkartSynopsisMetric(group.dailyBudget, 'money')}
                budget={group.dailyBudget}
                campaignLabel={group.label}
                onApply={(dailyBudget) =>
                  onUpdateCampaign(group.key, { daily_budget: dailyBudget })
                }
              />
            ) : (
              formatFlipkartSynopsisMetric(group.dailyBudget, 'money')
            )}
          </td>
          <td className={workspaceTableClass.td}>
            {group.campaignEditable && onUpdateCampaign ? (
              <FlipkartCampaignDateCell
                displayValue={formatIsoDisplayDate(group.startDate, '—')}
                isoDate={group.startDate}
                campaignLabel={group.label}
                kind="start"
                onApply={(startDate) =>
                  onUpdateCampaign(group.key, { start_date: startDate })
                }
              />
            ) : (
              formatIsoDisplayDate(group.startDate, '—')
            )}
          </td>
          <td className={workspaceTableClass.td}>
            {group.campaignEditable && onUpdateCampaign ? (
              <FlipkartCampaignDateCell
                displayValue={formatIsoDisplayDate(group.endDate, '—')}
                isoDate={group.endDate}
                campaignLabel={group.label}
                kind="end"
                onApply={(endDate) => onUpdateCampaign(group.key, { end_date: endDate })}
              />
            ) : (
              formatIsoDisplayDate(group.endDate, '—')
            )}
          </td>
        </>
      ) : null}
      {FLIPKART_SYNOPSIS_METRICS.map((metric) => (
        <td key={metric.key} className={cn(workspaceTableClass.td, 'text-right')}>
          <FlipkartSynopsisMetricValue
            current={group.current[metric.key]}
            previous={group.previous[metric.key]}
            hasPrevious={group.hasPrevious}
            format={metric.format}
          />
        </td>
      ))}
    </tr>
  );

  const totalsRow = totals ? (
    <tr className="bg-surface-muted font-semibold">
      {selection ? <FlipkartCheckboxCell /> : null}
      <td className={cn(workspaceTableClass.td, FLIPKART_LABEL_COL_CLASS)}>Total</td>
      {secondaryHeader ? (
        <td className={cn(workspaceTableClass.td, FLIPKART_LABEL_COL_CLASS)} />
      ) : null}
      {showPlacements ? <td className={workspaceTableClass.td} /> : null}
      {campaignMeta ? (
        <>
          <td className={workspaceTableClass.td} />
          <td className={workspaceTableClass.td} />
          <td className={workspaceTableClass.td} />
          <td className={workspaceTableClass.td} />
        </>
      ) : null}
      {FLIPKART_SYNOPSIS_METRICS.map((metric) => (
        <td key={metric.key} className={cn(workspaceTableClass.td, 'text-right')}>
          <FlipkartSynopsisMetricValue
            current={totals.current[metric.key]}
            previous={totals.previous[metric.key]}
            hasPrevious={totals.hasPrevious}
            format={metric.format}
            emphasize
          />
        </td>
      ))}
    </tr>
  ) : null;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-display text-headline-md text-foreground">{title}</h2>
        <div className="flex w-full max-w-xl flex-wrap items-end justify-end gap-3">
          {statusFiltering ? (
            <FilterSelect
              id={statusId}
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <option value="all">All</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </FilterSelect>
          ) : null}
          <div className="min-w-[12rem] flex-1 max-w-xs">
            <FilterSearch
              id={searchId}
              label={title}
              value={query}
              onChange={setQuery}
              placeholder={`Search ${title.toLowerCase()}…`}
            />
          </div>
        </div>
      </div>
      {selection && selection.selectedIds.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-selection-border bg-selection-bar px-5 py-2.5">
          <span className="text-body-md font-semibold text-brand-ink" role="status">
            {selection.selectedIds.length} selected
          </span>
          {selection.actions.length > 0 ? (
            <details className="group relative">
              <summary className="flex h-7 cursor-pointer list-none items-center gap-1 rounded-lg border border-selection-divider bg-surface px-2.5 text-body-sm font-medium text-brand-ink [&::-webkit-details-marker]:hidden">
                Actions
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
              </summary>
              <div className="absolute left-0 z-20 mt-1 w-52 rounded-xl border border-border bg-surface p-1.5 shadow-lg">
                {selection.actions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    disabled={selection.actionDisabled || action.disabled}
                    onClick={(event) => {
                      const details = event.currentTarget.closest('details');
                      if (details) details.open = false;
                      selection.onAction(action.id);
                    }}
                    className="flex w-full rounded-lg px-2.5 py-2 text-left text-body-md text-foreground hover:bg-surface-muted disabled:cursor-not-allowed disabled:text-foreground-muted"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </details>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={selection.onClear}
            disabled={selection.actionDisabled}
          >
            Clear
          </Button>
        </div>
      ) : null}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner className="h-6 w-6" />
          </div>
        ) : isError ? (
          <div className="px-6 py-8 text-center">
            <p className="font-medium text-foreground">Could not load {title.toLowerCase()}</p>
            {errorMessage ? (
              <p className="mt-2 text-body-sm text-foreground-muted">{errorMessage}</p>
            ) : null}
            {onRetry ? (
              <Button type="button" className="mt-4" onClick={onRetry}>
                Retry
              </Button>
            ) : null}
          </div>
        ) : virtualize ? (
          <VirtualizedTable
            rows={visible}
            renderRow={renderGroupRow}
            header={tableHead}
            footer={totalsRow}
            estimateRowHeight={showPlacements ? PLACEMENT_ROW_HEIGHT : SYNOPSIS_ROW_HEIGHT}
            maxHeight="28rem"
            tableClassName={workspaceTableClass.table}
            tbodyClassName=""
            wrapperClassName="max-w-full min-w-0"
            scrollClassName="app-table-scroll custom-scrollbar isolate max-w-full min-w-0"
          />
        ) : (
          <div className="app-table-scroll custom-scrollbar isolate max-h-[28rem] max-w-full min-w-0 overflow-x-auto overflow-y-auto">
              <table className={workspaceTableClass.table}>
                {tableHead}
                <tbody>
                  {emptyMessage ? (
                    <tr>
                      <td
                        colSpan={columnCount}
                        className={cn(workspaceTableClass.td, 'px-3 text-foreground-muted')}
                      >
                        {emptyMessage}
                      </td>
                    </tr>
                  ) : (
                    visible.map(renderGroupRow)
                  )}
                  {totalsRow}
                </tbody>
              </table>
          </div>
        )}
      </Card>
    </section>
  );
}

function FlipkartPlacementsCell({
  placements,
}: {
  placements: FlipkartSynopsisGroup['placements'];
}) {
  if (!placements?.length) {
    return <span className="text-foreground-muted">—</span>;
  }

  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
      {placements.map((item) => (
        <div key={item.id} className="flex items-start justify-between gap-3">
          <span className="text-foreground-muted">{item.label}</span>
          <span className="shrink-0 text-right">
            <span className="block tabular-nums text-brand">
              {formatFlipkartSynopsisMetric(item.bid, 'money')}
            </span>
            {item.minimum != null ? (
              <span className="block tabular-nums text-body-sm text-foreground-muted">
                min {formatFlipkartSynopsisMetric(item.minimum, 'money')}
              </span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function SortHead({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = 'left',
  className,
}: {
  label: string;
  sortKey: FlipkartSynopsisSortKey;
  activeKey: FlipkartSynopsisSortKey;
  dir: 'asc' | 'desc';
  onSort: (key: FlipkartSynopsisSortKey) => void;
  align?: 'left' | 'right';
  className?: string;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      className={cn(workspaceTableClass.th, align === 'right' && 'text-right', className)}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={cn(
          'inline-flex max-w-full items-center gap-1 uppercase tracking-[0.14em]',
          align === 'right' && 'ml-auto'
        )}
        onClick={() => onSort(sortKey)}
      >
        <span className="min-w-0 truncate">{label}</span>
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
          )
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" aria-hidden />
        )}
      </button>
    </th>
  );
}
